import { getDayStart } from 'pages/components/Graphs/hooks';
import { getLogActorIdentity } from 'pages/Logs/logActor';
import { Log } from 'pages/Logs/hooks';
import { MetricLog } from './redux';

const bucketKey = (log: Log, dayStartMs: number, byActor?: boolean) => (
  `${dayStartMs}\0${log.type}\0${log.department || ''}\0${log.inventory_type || ''}\0${log.inventory_item_id || ''}${
    byActor ? `\0${getLogActorIdentity(log) || ''}` : ''
  }`
);

/**
 * Roll raw activity logs into day + type (+ department / inventory) metric buckets.
 * Same merge rules used for Metrics charts / totals.
 * O(n log n) sort once, then O(n) Map merges — safe for large organizations.
 * `byActor` keeps staff identity so Users charts can split activity per person.
 */
export const aggregateLogsToMetricLogs = (
  logs: Log[] | null | undefined,
  options?: { byActor?: boolean },
): MetricLog[] => {
  if (!logs?.length) return [];

  const sorted = [...logs]
    .filter((log) => Boolean(log.date) && Number.isFinite(+new Date(log.date as string)))
    .sort((a, b) => +new Date(a.date as string) - +new Date(b.date as string));
  const bucketIndex = new Map<string, number>();
  const newLogs: MetricLog[] = [];

  for (let i = 0; i < sorted.length; i += 1) {
    const log = sorted[i];
    if (!log.date) continue;
    const dayStartMs = +getDayStart(new Date(log.date));
    const key = bucketKey(log, dayStartMs, options?.byActor);
    const value = (+log.value ? +log.value : 0);
    const ind = bucketIndex.get(key);

    if (ind !== undefined) {
      const prev = newLogs[ind];
      newLogs[ind] = {
        ...prev,
        date: log.date,
        value: (+prev.value + value).toString(),
        count: prev.count + 1,
        units: (log.units != null || prev.units != null)
          ? (Number(prev.units) || 0) + (Number(log.units) || 0)
          : prev.units,
      };
    } else {
      bucketIndex.set(key, newLogs.length);
      newLogs.push({
        ...log,
        value: value.toString(),
        count: 1,
      });
    }
  }

  return newLogs;
};
